import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { now } from '../../lib/clock';
import { cn } from '../../lib/cn';
import { formatActivityTime } from '../../lib/format';
import { useLayer } from '../../lib/layers';
import { useUndo } from '../ui/Toaster';
import { useAuditLog } from './queries';
import type { ActivityFocus, AuditEntry } from './queries';
import { RouteErrorMessage } from './RouteError';

/** Server undo window (server/src/lib/undo.ts UNDO_WINDOW_MS). */
export const UNDO_WINDOW_MS = 10 * 60 * 1000;
const ACTIVITY_LIMIT = 200;
/** The focused rows stay highlighted this long, then fade (design: 600 ms + 320 ms transition). */
const HIGHLIGHT_MS = 600;

export interface ActivityRow {
  readonly id: string;
  readonly time: string;
  readonly text: string;
  readonly label: string | null;
  readonly where: string | null;
  readonly by: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly undoToken: string | null;
  readonly canUndo: boolean;
  readonly undone: boolean;
}

/**
 * Activity rows from the audit log, newest first. Rows recording an undo are folded into the row
 * they reverted; a bulk change (one token, many records) shows once; Undo is offered while the
 * token is inside its 10-minute window.
 */
export function toActivityRows(entries: readonly AuditEntry[], at: Date): ActivityRow[] {
  const undoneTokens = new Set(entries.filter((e) => e.action === 'undo').map((e) => e.entityId));
  const seenTokens = new Set<string>();
  const rows: ActivityRow[] = [];
  for (const entry of entries) {
    if (entry.action === 'undo') continue;
    const token = entry.undoToken;
    if (token && seenTokens.has(token)) continue;
    if (token) seenTokens.add(token);
    const undone = !!token && undoneTokens.has(token);
    const fresh = at.getTime() - Date.parse(entry.at) < UNDO_WINDOW_MS;
    rows.push({
      id: entry.id,
      time: formatActivityTime(entry.at, at),
      text: entry.summary ?? `${entry.action} ${entry.entityType}`,
      label: entry.label ?? null,
      where: entry.where ?? null,
      by: entry.by ?? 'Вы',
      entityType: entry.entityType,
      entityId: entry.entityId,
      undoToken: token,
      canUndo: !!token && !undone && fresh,
      undone,
    });
  }
  return rows;
}

interface ActivityPanelProps {
  readonly focus: ActivityFocus | null;
  readonly onClose: () => void;
}

/** Activity panel (design Main.dc.html:123-144): 400px on the right, newest changes with Undo. */
export function ActivityPanel({ focus, onClose }: ActivityPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const query = useAuditLog(ACTIVITY_LIMIT, true, focus);
  const rows = query.data ? toActivityRows(query.data.items, now()) : [];
  const highlighted = useHighlight(focus, listRef, rows.length);
  useLayer({ kind: 'drawer', panelRef, onClose, initialFocus: 'panel' });

  return (
    <aside
      ref={panelRef}
      role="dialog"
      aria-label="Активность"
      tabIndex={-1}
      className="fixed bottom-0 right-0 top-0 z-[45] flex w-[400px] flex-col border-l border-border-1 bg-bg-3 shadow-lg outline-none"
    >
      <div className="flex items-center gap-3 border-b border-border-1 pb-3.5 pl-5 pr-4 pt-4">
        <div className="flex-1">
          <div className="text-[16px] font-semibold">Активность</div>
          <div className="text-fg-2">Изменения за сессию можно отменить</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="size-8 rounded-ctl border-0 bg-transparent text-[18px] text-fg-2 hover:bg-neutral-100"
        >
          ×
        </button>
      </div>
      <div ref={listRef} className="relative flex-1 overflow-y-auto">
        {query.isPending && <div className="px-5 py-6 text-fg-3">Загрузка…</div>}
        {query.isError && (
          <div className="px-5 py-4">
            <RouteErrorMessage error={query.error} onRetry={() => void query.refetch()} />
          </div>
        )}
        {query.isSuccess && rows.length === 0 && <div className="px-5 py-6 text-fg-3">Событий нет.</div>}
        {rows.map((row) => (
          <ActivityRowView key={row.id} row={row} highlighted={highlighted && isFocused(row, focus)} />
        ))}
      </div>
    </aside>
  );
}

const isFocused = (row: ActivityRow, focus: ActivityFocus | null): boolean =>
  !!focus && row.entityType === focus.entityType && row.entityId === focus.entityId;

/** Highlights the focused record's rows briefly and scrolls the first one 96px below the top. */
function useHighlight(
  focus: ActivityFocus | null,
  listRef: RefObject<HTMLDivElement | null>,
  rowCount: number,
): boolean {
  const [on, setOn] = useState(true);
  useEffect(() => {
    if (!focus || rowCount === 0) return undefined;
    const first = listRef.current?.querySelector<HTMLElement>('[data-hl="1"]');
    if (first && listRef.current) listRef.current.scrollTop = Math.max(0, first.offsetTop - 96);
    const timer = window.setTimeout(() => setOn(false), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [focus, listRef, rowCount]);
  return on;
}

function ActivityRowView({ row, highlighted }: { readonly row: ActivityRow; readonly highlighted: boolean }) {
  const undo = useUndo();
  const [pending, setPending] = useState(false);
  const onUndo = async () => {
    if (!row.undoToken) return;
    setPending(true);
    try {
      await undo(row.undoToken, row.text);
    } finally {
      setPending(false);
    }
  };
  return (
    <div
      data-hl={highlighted ? '1' : '0'}
      className={cn(
        'flex items-start gap-3 border-b border-border-1 px-5 py-3 transition-[background] duration-[320ms] ease-[cubic-bezier(0.2,0,0,1)]',
        highlighted ? 'bg-pi-coral-tint' : 'bg-transparent',
        row.undone && 'opacity-55',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {row.label && <span className="text-[12px] font-semibold text-fg-2">{row.label}</span>}
        <span className={cn('leading-[1.4]', row.undone && 'line-through')}>{row.text}</span>
        {row.where && <span className="truncate text-[12px] text-fg-2">{row.where}</span>}
        <span className="text-[12px] text-fg-3">
          {row.time} · {row.by}
        </span>
      </div>
      {row.canUndo && (
        <button
          type="button"
          disabled={pending}
          onClick={() => void onUndo()}
          className="h-7 flex-none rounded-ctl border border-border-1 bg-bg-3 px-2.5 text-[12px] font-semibold text-fg-1 hover:bg-bg-1 disabled:opacity-40"
        >
          Отменить
        </button>
      )}
      {row.undone && <span className="text-[12px] text-fg-3">Отменено</span>}
    </div>
  );
}
